import { useEffect, useState, useCallback } from "react";
import { AppShell } from "../components/layout/AppShell";
import { Badge, Card, ErrorState } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";

interface EmulatorView { allowEmulators: boolean; allowDeveloperOptions: boolean; attestMode: string; serverSideEnforced: boolean; developerOptionsAuthoritative: boolean; notes: string[] }

/** Round 56 - Security: "Allow App on Emulators" ON/OFF. Applies without an app update (the app refreshes its policy on start and every ~10 min). */
export function EmulatorAccessPage() {
  const { showToast } = useToast();
  const [v, setV] = useState<EmulatorView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    setError(null);
    apiRequest<EmulatorView>("/admin/emulator-access").then(setV).catch((e) => setError(e instanceof ApiError ? e.message : "could not load"));
  }, []);
  useEffect(load, [load]);

  async function set(field: "allowEmulators" | "allowDeveloperOptions", allow: boolean) {
    setSaving(true);
    try {
      setV(await apiRequest<EmulatorView>("/admin/emulator-access", { method: "POST", body: { [field]: allow } }));
      const what = field === "allowEmulators" ? "Emulators" : "Developer options";
      showToast(`${what}: ${allow ? "allowed" : "blocked"}`, "success");
    } catch (e) {
      showToast(e instanceof ApiError ? e.message : "could not save", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppShell title="Device Access">
      {error ? (
        <ErrorState message={error} onRetry={load} />
      ) : !v ? (
        <p className="text-sm text-ink-muted">Loading…</p>
      ) : (
        <div className="max-w-3xl space-y-5">
          <Card className="space-y-4 p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-sm font-semibold text-ink">Allow App on Emulators</h2>
                <p className="mt-1 text-xs text-ink-faint">ON: supported Android emulators can use the app. OFF: detected emulators see a security message and cannot continue.</p>
              </div>
              <Badge tone={v.allowEmulators ? "success" : "danger"}>{v.allowEmulators ? "ON" : "OFF"}</Badge>
            </div>
            <div className="flex gap-2">
              <Button variant={v.allowEmulators ? "primary" : "secondary"} disabled={saving} onClick={() => set("allowEmulators", true)}>Allow emulators</Button>
              <Button variant={!v.allowEmulators ? "danger" : "secondary"} disabled={saving} onClick={() => set("allowEmulators", false)}>Block emulators</Button>
            </div>
</Card>

          <Card className="space-y-4 p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-sm font-semibold text-ink">Allow App When Developer Options Are Enabled</h2>
                <p className="mt-1 text-xs text-ink-faint">ON: the app works even if Android Developer options are switched on. OFF: users with Developer options on see a message asking them to turn it off. Independent of the emulator switch above.</p>
              </div>
              <Badge tone={v.allowDeveloperOptions ? "success" : "danger"}>{v.allowDeveloperOptions ? "ON" : "OFF"}</Badge>
            </div>
            <div className="flex gap-2">
              <Button variant={v.allowDeveloperOptions ? "primary" : "secondary"} disabled={saving} onClick={() => set("allowDeveloperOptions", true)}>Allow</Button>
              <Button variant={!v.allowDeveloperOptions ? "danger" : "secondary"} disabled={saving} onClick={() => set("allowDeveloperOptions", false)}>Block</Button>
            </div>
            {v.notes.map((n) => <p key={n} className="rounded-lg bg-warn-bg p-3 text-xs text-warn">{n}</p>)}
            <p className="text-xs text-ink-faint">Integrity attestation mode: <span className="font-semibold text-ink">{v.attestMode}</span> {v.serverSideEnforced ? "(server-side verdict enforced)" : ""}</p>
          </Card>

          <Card className="space-y-2 p-5 text-xs text-ink-muted">
            <h2 className="text-sm font-semibold text-ink">How this works, and its limits</h2>
            <ul className="list-disc space-y-1 pl-5">
              <li>Both switches are checked on the device at start-up, on every screen change and on login, and the device also reports its state to the server, which then refuses protected actions (purchases, wallet, rooms, messages, profile edits) from a device that breaks a rule that is OFF.</li>
              <li><b>Emulators:</b> the strongest check is Google Play Integrity (needs the app on Google Play, a linked Google Cloud project, and Security → attest mode <b>enforce</b>). Without it the server only acts on what the app reports, which a modified app can hide.</li>
              <li><b>Developer options:</b> Google gives no verdict for this. It is read from the Android setting by the app and reported to the server; a rooted or modified device can hide it. Treat it as a deterrent, not a guarantee.</li>
              <li>The state is stored per account, from the latest report. If the same account is used on an emulator and on a phone, the most recent report wins.</li>
              <li>Debug builds of the app ignore both rules (developers always have them); test on a release build.</li>
              <li>Changes reach a running app within about 2 minutes (and on the next start); users who fix their device press Retry on the message screen.</li>
              <li>No detection is perfect: some emulators pass as real devices and some unusual physical devices look suspicious. Test on your own devices before turning a switch OFF for everyone.</li>
            </ul>
          </Card>
        </div>
      )}
    </AppShell>
  );
}
